<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { DateTime } from 'luxon';
import { useI18n } from 'vue-i18n';
import { usePromoComposable } from '@/components/admin/usePromoComposable';
import { useViewComposable } from '@/views/composable';
import { useWorksheetComposable } from '@/components/modal/worksheet/useWorksheetComposable';
import { useLogStore } from '@/store/LogStore';
import { usePackageManagementStore } from '@/store/PackageManagementStore';
import {
  CUSTOMER_QUOTE_FORM,
  PDF,
  PROMOS,
  QUOTE_TO_BOOK_TERM,
  QUOTE_CONFIGURATION,
  ROLE,
  EMAIL_CONFIGURATION,
  EXPIRY_CONFIGURATION,
  OPEN_JAW_CONFIGURATION,
  RULE_ASSIGNMENT,
  FTV_CONFIGURATION,
  ADMIN_SETUP,
  MARGIN_SAFETY,
  CALENDAR_CONFIGURATION,
  AIR,
  AIR_ONLY,
  HOTEL,
  HOTEL_ONLY,
  MAX_ROOM_CONFIGURATION,
  OPTION_CONFIGURATION,
  TOUR
} from '@/constants';
import { useToast } from 'primevue/usetoast';
import Accordion from 'primevue/accordion';
import AccordionContent from 'primevue/accordioncontent';
import AccordionHeader from 'primevue/accordionheader';
import AccordionPanel from 'primevue/accordionpanel';
import RuleHistoryFilter from '@/components/ruleHistory/RuleHistoryFilter.vue';
import RuleHistoryTable from '@/components/ruleHistory/RuleHistoryTable.vue';

const props = defineProps<{
  ruleContent: any;
  groupTypes?: any;
}>();

const { t } = useI18n({ useScope: 'global' });
const { buttonIconExpand, buttonIconCollapse } = usePromoComposable();
const { loadingIcon } = useWorksheetComposable();
const { handleCheckQuoteType, multiDataRender, multiDataConfigItemRender, checkCalendarConfig } = useViewComposable();
const toast = useToast();
const logStore = useLogStore();
const isLoading = ref(false);
const isCloseTab = ref(false);
const ruleHistoryData = ref<any>([]);
const ruleData = ref<any>([]);
const termConditionTypes = [CUSTOMER_QUOTE_FORM, PDF, QUOTE_TO_BOOK_TERM];
const specialTypes = [PROMOS, ROLE, ADMIN_SETUP, MARGIN_SAFETY, EXPIRY_CONFIGURATION, CALENDAR_CONFIGURATION, EMAIL_CONFIGURATION, MAX_ROOM_CONFIGURATION];
const specialTypeFilters = [MARGIN_SAFETY, EXPIRY_CONFIGURATION, EMAIL_CONFIGURATION, ADMIN_SETUP, CALENDAR_CONFIGURATION, OPTION_CONFIGURATION, MAX_ROOM_CONFIGURATION];
const minDateDefault = new Date(DateTime.utc().minus({ days: 30 }));
const maxDateDefault = new Date();
const initFilterDate = ref({});
const filterTypeInit = ref({});
const activePanel = ref();
const destCode = ref();
const configurableLabels = [t('common.labels.configurable_item_type'), t('quote_config.flight_type'), t('gqt_admin_setup.gqt_admin_setup_type'), t('common.labels.quote_type')];
const isQuoteConfig = computed(() => configurableLabels.includes(multiDataRender(props.ruleContent.type)?.label));
const packageManagementStore = usePackageManagementStore();

const convertType = (type: string) => {
  const typesCheck = [EMAIL_CONFIGURATION, EXPIRY_CONFIGURATION, MAX_ROOM_CONFIGURATION, OPEN_JAW_CONFIGURATION, CALENDAR_CONFIGURATION, RULE_ASSIGNMENT, FTV_CONFIGURATION, MARGIN_SAFETY, OPTION_CONFIGURATION];
  return typesCheck.includes(type) ? QUOTE_CONFIGURATION : type;
};
const filterData = (response: any, data: any, dataFilter: any) => {
  const configurableItemType = dataFilter.configurableItemType;

  if (configurableItemType.includes(OPTION_CONFIGURATION)) {
    return response.filter((item: any) => {
      const itemTypeConfig = item.extension.toLowerCase();
      return data?.configurableItemType
        .map((keyword: string) => {
          if (keyword === TOUR) {
            return 'tours';
          }
          return `${keyword} quote`.toLowerCase();
        })
        .some((keyword: string) => itemTypeConfig && itemTypeConfig.includes(keyword));
    });
  }

  const typeConfig = configurableItemType.includes(MARGIN_SAFETY) ? 'extension' : 'rule';
  return response.filter((item: any) => data?.configurableItemType.some((keyword: any) => item[typeConfig] && (item[typeConfig].includes(keyword) || item[typeConfig].includes('SPECIAL_PROMO_CONFIG'))));
};

const filterDataCalendar = (response: any, data: any) => {
  const filteredData = response.filter((item: any) => {
    const extensions = item.extension?.split(', ').map((ext: string) => ext.trim());
    const hasSelectedExtensions = data.groupType?.some((ext: string) => extensions?.includes(ext)) && !extensions?.some((ext: string) => !data.groupType?.includes(ext));
    let normalizedQuoteType = item.quoteType;
    if (normalizedQuoteType === HOTEL_ONLY) {
      normalizedQuoteType = HOTEL;
    }
    if (normalizedQuoteType === AIR_ONLY) {
      normalizedQuoteType = AIR;
    }

    const isQuoteTypeAllowed = data.calendarQuotes?.includes(normalizedQuoteType);

    return hasSelectedExtensions && isQuoteTypeAllowed;
  });
  return filteredData;
};

const handleOpenView = (dataFilter: any, dataConfigItemFilter: any, filterType: any) => {
  isLoading.value = true;
  logStore
    .historyRuleLog(convertType(props.ruleContent.type), dataFilter)
    .then((response: any) => {
      ruleHistoryData.value = response;
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
    })
    .finally(() => {
      isLoading.value = false;
      if (specialTypeFilters.includes(props.ruleContent.type)) {
        ruleData.value = filterData(ruleHistoryData.value, dataConfigItemFilter, filterType);
      }
      if (checkCalendarConfig(props.ruleContent.type)) {
        ruleData.value = filterDataCalendar(ruleHistoryData.value, dataConfigItemFilter);
      }
    });
};

const handleFilterData = (dataFilter: any, dataConfigItemFilter: any) => {
  isCloseTab.value = false;
  const payload = dataFilter.quoteType
    ? { ...dataFilter, quoteType: dataFilter.quoteType.join(',') }
    : {
        ...dataFilter,
        [multiDataRender(props.ruleContent.type)?.key]: dataFilter[multiDataRender(props.ruleContent.type)?.key]?.join(',')
      };
  handleOpenView(payload, dataConfigItemFilter, dataFilter);
};

const renderInit = () => {
  return termConditionTypes.includes(props.ruleContent.type)
    ? {
        quoteType: handleCheckQuoteType(props.ruleContent.type),
        fromDate: minDateDefault,
        toDate: maxDateDefault
      }
    : {
        fromDate: minDateDefault,
        toDate: maxDateDefault,
        [multiDataRender(props.ruleContent.type)?.key]: multiDataRender(props.ruleContent.type)?.initFilter
      };
};

const renderTypeInit = () => {
  return specialTypeFilters.includes(props.ruleContent.type)
    ? {
        fromDate: minDateDefault,
        toDate: maxDateDefault,
        [multiDataConfigItemRender(props.ruleContent.type)?.key]: multiDataConfigItemRender(props.ruleContent.type)?.initFilter
      }
    : {};
};

const renderCalendar = () => {
  return specialTypeFilters.includes(props.ruleContent.type)
    ? {
        fromDate: minDateDefault,
        toDate: maxDateDefault,
        type: '',
        [multiDataConfigItemRender(props.ruleContent.type)?.key]: multiDataConfigItemRender(props.ruleContent.type)?.initFilter,
        groupType: props.groupTypes.map((item: any) => item.sk),
        quoteType: termConditionTypes.includes(props.ruleContent.type) ? handleCheckQuoteType(props.ruleContent.type) : '',
        calendarQuotes: multiDataConfigItemRender(props.ruleContent.type)?.initFilter.map((item: any) => item.value)
      }
    : {};
};

const getListDestination = () => {
  isLoading.value = true;
  packageManagementStore
    .getListDestination()
    .then((res: any) => {
      destCode.value = res;
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
    })
    .finally(() => {
      isLoading.value = false;
    });
};

watch(
  () => props.ruleContent,
  () => {
    initFilterDate.value = renderInit();
    filterTypeInit.value = checkCalendarConfig(props.ruleContent.type) ? renderCalendar() : renderTypeInit();
    getListDestination();
  },
  { deep: true, immediate: true }
);
</script>

<template>
  <div class="rule-history-content">
    <Accordion
      v-model:value="activePanel"
      @tabClose="isCloseTab = true"
      @tabOpen="handleFilterData(initFilterDate, filterTypeInit)"
      :expandIcon="'fa-light fa-3x fa-circle-plus text-primary'"
      :collapseIcon="'fa-light fa-3x fa-circle-minus text-primary'"
    >
      <AccordionPanel value="0">
        <AccordionHeader>
          {{ ruleContent.label }}
          <div class="absolute right-0 px-3 flex align-items-center h-full expand-icon">
            <img class="button-icon" v-if="activePanel != 0" :src="buttonIconExpand" alt="Expand" />
            <img class="button-icon" v-else :src="buttonIconCollapse" alt="Collapse" />
          </div>
        </AccordionHeader>
        <AccordionContent>
          <div>
            <RuleHistoryFilter
              :filterQuoteType="termConditionTypes.includes(ruleContent.type)"
              :specialTypes="specialTypes.includes(ruleContent.type)"
              @handleFilterData="(dataFilter, dataConfigItemFilter) => handleFilterData(dataFilter, dataConfigItemFilter)"
              :type="ruleContent.type"
              :isCloseTab="isCloseTab"
              :listField="ruleContent.filterOption"
              :groupTypes="groupTypes"
            />
          </div>
          <div>
            <div v-if="isLoading" class="flex justify-content-center">
              <div class="w-3rem h-3rem rotate"><img class="w-full" :alt="$t('common.generic.loading')" :src="loadingIcon" /></div>
            </div>
            <div v-else>
              <RuleHistoryTable
                :destCode="destCode"
                :ruleHistory="isQuoteConfig && specialTypeFilters.includes(ruleContent.type) ? ruleData : ruleHistoryData"
                :isTermConditionTypes="termConditionTypes.includes(ruleContent.type)"
                :type="ruleContent.type"
                :groupTypes="groupTypes"
              />
            </div>
          </div>
        </AccordionContent>
      </AccordionPanel>
    </Accordion>
  </div>
</template>
